/**
 * Definición ÚNICA de «sin clasificar» (Panel, Auditoría, Alertas, Estado SUNAT y Por Clasificar).
 *
 *   Universo : transacciones activas del período (no anuladas, no borradas).
 *   Sin clasificar = status ≠ 'approved'  (no confirmada por el usuario).
 *   Aprobadas      = status = 'approved'.   → aprobadas + sin clasificar = total (excluyentes).
 *
 * Desglose de «sin clasificar» en tres causas EXCLUYENTES (en este orden de prioridad):
 *   1. sin_cuenta          — sin cuenta asignada o proveedor pendiente (mappingSource 'pending').
 *   2. baja_confianza      — status 'review' (confianza < 90) con cuenta asignada.
 *   3. auto_sin_confirmar  — clasificada automáticamente, pendiente de confirmar.
 *
 * «Con errores» (confianza < 80) es una MARCA APARTE: se cuenta completa (sin tope), se solapa con
 * aprobadas y con sin clasificar y no forma parte del total.
 */

export interface ClasificableTx {
  status?: string | null;
  cuenta?: string | null;
  mappingSource?: string | null;
  confidence?: number | null;
  anulada?: boolean | null;
  isDeleted?: boolean | null;
}

export type CausaSinClasificar = 'sin_cuenta' | 'baja_confianza' | 'auto_sin_confirmar';

/** Umbral de «con errores»: confianza menor a este valor. */
export const UMBRAL_ERROR_CONFIANZA = 80;

/** ¿Pertenece al universo? Activa: ni anulada, ni borrada, ni status 'annulled'. */
function esActiva(tx: ClasificableTx): boolean {
  return !tx.anulada && !tx.isDeleted && tx.status !== 'annulled';
}

export function isSinClasificar(tx: ClasificableTx): boolean {
  return esActiva(tx) && tx.status !== 'approved';
}

export function causaSinClasificar(tx: ClasificableTx): CausaSinClasificar | null {
  if (!isSinClasificar(tx)) return null;
  if (!tx.cuenta || tx.mappingSource === 'pending') return 'sin_cuenta';
  if (tx.status === 'review') return 'baja_confianza';
  return 'auto_sin_confirmar';
}

export function tieneError(tx: ClasificableTx): boolean {
  return tx.confidence != null && tx.confidence < UMBRAL_ERROR_CONFIANZA;
}

export interface ResumenClasificacion {
  total: number;
  aprobadas: number;
  sinClasificar: number;
  porCausa: Record<CausaSinClasificar, number>;
  /** Marca aparte: se solapa con aprobadas y sin clasificar; no suma al total. */
  conErrores: number;
}

export function resumenClasificacion(txs: ClasificableTx[]): ResumenClasificacion {
  const r: ResumenClasificacion = {
    total: 0, aprobadas: 0, sinClasificar: 0, conErrores: 0,
    porCausa: { sin_cuenta: 0, baja_confianza: 0, auto_sin_confirmar: 0 },
  };
  for (const tx of txs) {
    if (!esActiva(tx)) continue;
    r.total++;
    if (tieneError(tx)) r.conErrores++;
    const causa = causaSinClasificar(tx);
    if (causa) { r.sinClasificar++; r.porCausa[causa]++; } else { r.aprobadas++; }
  }
  return r;
}
